import { Injectable } from '@angular/core';
import { ImageCheckSettings } from '../../models/image-check.models';

/**
 * Service for managing persistent storage of Image Check settings
 * Uses browser localStorage to persist configuration across sessions
 */
@Injectable({
  providedIn: 'root'
})
export class ImageCheckStorageService {
  private readonly STORAGE_KEY_ENDPOINT = 'imageCheck.endpoint';
  private readonly STORAGE_KEY_SETTINGS = 'imageCheck.settings';

  /**
   * Get the stored endpoint URL
   * @returns The stored endpoint URL or null if not set
   */
  getStoredEndpoint(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY_ENDPOINT);
    } catch (error) {
      console.error('Error reading endpoint from localStorage:', error);
      return null;
    }
  }

  /**
   * Store the endpoint URL
   * @param url The endpoint URL to store
   */
  setStoredEndpoint(url: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_ENDPOINT, url);
    } catch (error) {
      console.error('Error storing endpoint to localStorage:', error);
    }
  }

  /**
   * Clear the stored endpoint URL
   */
  clearStoredEndpoint(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY_ENDPOINT);
    } catch (error) {
      console.error('Error clearing endpoint from localStorage:', error);
    }
  }

  /**
   * Get the stored settings
   * @returns The stored settings or null if not set
   */
  getStoredSettings(): ImageCheckSettings | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_SETTINGS);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading settings from localStorage:', error);
      return null;
    }
  }

  /**
   * Store the settings
   * @param settings The settings to store
   */
  setStoredSettings(settings: ImageCheckSettings): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('Error storing settings to localStorage:', error);
    }
  }

  /**
   * Clear all stored settings
   */
  clearAll(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY_ENDPOINT);
      localStorage.removeItem(this.STORAGE_KEY_SETTINGS);
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }
}
